import { hardNavigation } from "../../utils/hard-navigation.util";
import { FormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { Component, OnInit } from "@angular/core";
import { FormBuilder, Validators } from "@angular/forms";
import { RxwebValidators } from "@rxweb/reactive-form-validators";

import { AuthService } from "src/app/core/core-services/services/auth.service";
import { DIALOG_WIDTHS, DialogService } from "src/app/core/core-services/services/dialog.service";
import { DataService } from "src/app/core/core-services/services/dataservices/data.service";
import { NotificationService } from "src/app/core/core-services/services/notification.service";

import { ApplicationConstants } from "src/app/core/core-services/constants/application.constants";
import { ValidationConstants } from "src/app/core/core-services/constants/validation.constants";
import {
  DeleteAccountDialogComponent,
  DeleteAccountDialogResult,
} from "src/app/vengreso/profile/delete-account-dialog/delete-account-dialog.component";
import type { VngBadgeVariant } from "@vengreso/angular-components";


@Component({
    selector: "app-profile",
    templateUrl: "./profile.component.html",
    styleUrls: ["./profile.component.scss"],
    standalone: false
})
export class ProfileComponent implements OnInit {
  profileData: any;
  form: FormGroup;
  pform: FormGroup;
  updating: boolean = false;
  deleting: boolean = false;

  // C3-5: password visibility toggles (04 §3.1 composition — vng-input has no
  // built-in mat-pass-toggle-visibility equivalent).
  showOldPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;

  readonly supportMailto = ApplicationConstants.SUPPORT_MAILTO;

  constructor(
    private fb: FormBuilder,
    private dialog: DialogService,
    private route: ActivatedRoute,
    private authService: AuthService,
    private dataService: DataService,
    private router: Router,
    private notification: NotificationService
  ) {
    if (this.router.url == "/profile") {
      this.router.navigate(["/settings"], { queryParams: { tab: "Profile" } });
    }
    this.form = this.fb.group({
      first_name: [null, [Validators.required, Validators.pattern(ValidationConstants.NON_DIGIT)]],
      last_name: [null, [Validators.required, Validators.pattern(ValidationConstants.NON_DIGIT)]],
      email: [null, [Validators.required]],
    });

    this.pform = this.fb.group({
      old_password: [null, [RxwebValidators.required()]],
      new_password: [
        null,
        [
          RxwebValidators.required(),
          RxwebValidators.minLength({ value: 8 }),
          RxwebValidators.maxLength({ value: 25 }),
          RxwebValidators.password({
            validation: { digit: true, specialCharacter: true },
          }),
        ],
      ],
      new_password_confirmation: [
        null,
        [
          RxwebValidators.required(),
          RxwebValidators.minLength({ value: 8 }),
          RxwebValidators.maxLength({ value: 20 }),
          RxwebValidators.password({
            validation: { digit: true, specialCharacter: true },
          }),
          RxwebValidators.compare({ fieldName: "new_password" }),
        ],
      ],
    });
  }

  get fullName(): string {
    return [this.profileData?.first_name, this.profileData?.last_name].filter(Boolean).join(" ").trim();
  }

  /** Two-letter monogram for the identity strip; falls back to the email. */
  get initials(): string {
    const first = (this.profileData?.first_name || "").trim();
    const last = (this.profileData?.last_name || "").trim();
    const monogram = `${first.charAt(0)}${last.charAt(0)}`.trim();

    return (monogram || (this.profileData?.email || "?").charAt(0)).toUpperCase();
  }

  get signInMethodBadge(): { label: string; variant: VngBadgeVariant } {
    return this.profileData?.is_password_set
      ? { label: "Email & password", variant: "default" }
      : { label: "Google sign-in", variant: "info" };
  }

  /** Save stays disabled until there is a valid, actually-changed value. */
  get canSaveProfile(): boolean {
    return this.form.valid && this.form.dirty && !this.updating;
  }

  // ngOnInit(): void {
  //   if (this.route.snapshot.data.profile && this.route.snapshot.data.profile.result) {
  //     this.profileData = this.route.snapshot.data.profile.result;
  //   }
  //   this.form.patchValue(this.profileData);
  // }

  ngOnInit(): void {
    this.dataService.get({ url: "v1/user", isLoader: true }).subscribe((response: any) => {
      if (response && response.result) {
        this.profileData = response.result;
        this.form.patchValue(this.profileData);
      }
    });
  }

  updateProfile(data) {
    this.updating = true;

    this.dataService.put({ url: "v1/user", isLoader: false, data }).subscribe(
      (response: any) => {
        if (response && response.result) {
          this.authService.updateUserDetail();
          this.notification.toast("Profile updated successfully! Effecting Change...");
          //Allow 0.4s for change to propagate in local store.
          setTimeout(() => {
            this.refresh();
          }, 400);
        }
      },
      (error) => {
        this.updating = false;
      },
      () => {
        this.updating = false;
      }
    );
  }

  refresh(): void {
    hardNavigation.reload();
  }

  /**
   * The danger zone is only a trigger — every warning, the consequence list
   * and the required `reason` live in {@link DeleteAccountDialogComponent},
   * which resolves to `{ reason }` only when the user goes through with it.
   */
  deleteProfile() {
    this.dialog
      .openDialogComponent<DeleteAccountDialogResult>(
        DeleteAccountDialogComponent,
        { class: "delete-account-dialog" },
        DIALOG_WIDTHS.md
      )
      .afterClosed()
      .subscribe((result) => result?.reason && this.delete(result.reason));
  }

  delete(reason: string) {
    this.deleting = true;

    // `reason` rides the request BODY: the API validates it out of the merged
    // input (Laravel FormRequest), and a body keeps a 1000-char free-text field
    // out of the URL and the access logs.
    this.dataService.delete({ url: "v1/user", body: { reason } }).subscribe({
      next: (response: any) => {
        if (response && response.result) {
          this.notification.toast("Profile deleted successfully!");
          this.authService.redirectAfterLogout(true);
          return;
        }
        this.deleting = false;
      },
      error: () => {
        this.deleting = false;
      },
    });
  }

  // C3-5: mat-password-strength (04 §3.1) is refuted as a gap — compose the
  // visible strength bar + rules checklist from vng-progress-bar/vng-icon
  // primitives. 5 equally-weighted criteria (20 points each, matching the
  // replaced library's default 5-rule config: length/lower/upper/digit/special).
  hasLowerCase(password: string): boolean {
    return /[a-z]/.test(password || "");
  }

  hasUpperCase(password: string): boolean {
    return /[A-Z]/.test(password || "");
  }

  hasDigit(password: string): boolean {
    return /[0-9]/.test(password || "");
  }

  hasSpecialChar(password: string): boolean {
    return /[^A-Za-z0-9]/.test(password || "");
  }

  hasMinLength(password: string): boolean {
    return (password || "").length >= 8;
  }

  passwordStrength(password: string): number {
    const checks = [
      this.hasLowerCase(password),
      this.hasUpperCase(password),
      this.hasDigit(password),
      this.hasSpecialChar(password),
      this.hasMinLength(password),
    ];
    return checks.filter(Boolean).length * 20;
  }

  /** The same 5 criteria, as rows — the template used to repeat each by hand. */
  passwordRules(password: string): { label: string; met: boolean }[] {
    return [
      { label: "contains at least one lower character", met: this.hasLowerCase(password) },
      { label: "contains at least one upper character", met: this.hasUpperCase(password) },
      { label: "contains at least one digit character", met: this.hasDigit(password) },
      { label: "contains at least one special character", met: this.hasSpecialChar(password) },
      { label: "contains at least 8 characters", met: this.hasMinLength(password) },
    ];
  }

  passwordStrengthLabel(password: string): string {
    if (!password) {
      return "";
    }

    const strength = this.passwordStrength(password);

    if (strength < 40) {
      return "Weak";
    }

    return strength < 100 ? "Getting there" : "Strong";
  }

  updatePassword(data) {
    this.dataService.post({ url: "v1/user/auth/password/change", data }).subscribe((response: any) => {
      if (response && response.result) {
        this.notification.toast("Password updated successfully!");
        this.authService.logout();
      }
    });
  }
}
